import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router';
import {
  COACH_NAME,
  PLAN_FOCUS_COPY,
  exerciseLink,
  currentPicks,
  type PlanFocus,
  type SessionSummary,
} from '@erectus/shared';
import { Card, SectionTitle } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { EXERCISE_LIBRARY } from '@/lib/repo.js';
import { ExerciseIllustration } from '@/features/exercises/ExerciseIllustration.js';

/**
 * Darwin's picks from the whole Progress picture — at most three, chosen by
 * `currentPicks` (packages/shared/src/plan.ts), the same function the
 * exercise reminder and the rest-break banner use, so the push and this card always name the same
 * movements. The report's coaching card covers single sessions.
 */
export function DarwinPicks({ sessions }: { sessions: SessionSummary[] }): ReactNode {
  const plan = useMemo(() => currentPicks(sessions, EXERCISE_LIBRARY), [sessions]);
  if (plan.picks.length === 0) return null;

  // One line of "why" per focus, in pick order.
  const reasons: PlanFocus[] = [];
  for (const pick of plan.picks) if (!reasons.includes(pick.focus)) reasons.push(pick.focus);

  return (
    // Anchor for the exercise reminder's tap (/progress#darwin-picks).
    <Card id="darwin-picks" className="scroll-mt-4 space-y-3">
      <div>
        <SectionTitle>{COACH_NAME}&apos;s picks this week</SectionTitle>
        <p className="mt-1 text-sm text-ink-soft">
          {reasons.map((focus) => PLAN_FOCUS_COPY[focus]).join(' ')}
        </p>
      </div>
      <ul className="space-y-2">
        {plan.picks.map((pick) => {
          const exercise = EXERCISE_LIBRARY.find((e) => e.id === pick.exerciseId);
          if (!exercise) return null;
          return (
            <li key={exercise.id}>
              <Link
                to={exerciseLink(exercise.id)} state={{ from: 'progress' }}
                className="flex items-center gap-3 rounded-xl bg-surface p-2 pr-3"
              >
                <ExerciseIllustration id={exercise.id} fallback={exercise.illustration} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">{exercise.name}</span>
                  <span className="block text-xs text-muted">{exercise.reps}</span>
                </span>
                <Icon name="back" size={16} className="shrink-0 rotate-180 text-muted" />
              </Link>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted">
        From your last {plan.sessionCount === 1 ? 'session' : `${plan.sessionCount} sessions`}. They
        rotate weekly.
      </p>
    </Card>
  );
}
